/**
 * FolderPickerDialog (DESIGN.md T10, §18): choose a target folder for "Move to folder…" and
 * "Copy to folder…" (also `Ctrl+Shift+V`). The shared EntityPicker over the folder tree (name and
 * path); folders in `disabledIds` (e.g. the current one) and structural rows cannot be chosen.
 */
import { useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/dialog';
import { EntityListPicker, type EntityOption } from '../composites/entity-picker';
import type { FolderTreeItem } from './mail-panes';
import type { RecipientInputLabels } from './recipient-input';

export interface FolderPickerLabels {
  title: string;
  search: string;
  empty: string;
  description?: string;
}

export interface FolderPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: readonly FolderTreeItem[];
  disabledIds?: readonly string[];
  onPick: (folderId: string) => void;
  labels: FolderPickerLabels;
  /** Labels of the shared entity picker. */
  pickerLabels: RecipientInputLabels;
}

/** Folder rows with their ancestry as the identity line ("Inbox / Projects"); tree order gives the parents. */
function folderOptions(folders: readonly FolderTreeItem[], disabledIds: readonly string[]): EntityOption<string>[] {
  const trail: string[] = [];
  const options: EntityOption<string>[] = [];
  for (const folder of folders) {
    trail.length = folder.depth ?? 0;
    trail.push(folder.name);
    if (folder.selectable === false || disabledIds.includes(folder.id)) continue;
    options.push({ id: folder.id, label: folder.name, identity: folder.description ?? trail.join(' / ') });
  }
  return options;
}

export function FolderPickerDialog({ open, onOpenChange, folders, disabledIds = [], onPick, labels, pickerLabels }: FolderPickerDialogProps) {
  const items = useMemo(() => folderOptions(folders, disabledIds), [folders, disabledIds]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-4">
        <DialogTitle>{labels.title}</DialogTitle>
        <DialogDescription className={labels.description ? 'text-sm text-muted-foreground' : 'sr-only'}>{labels.description ?? labels.search}</DialogDescription>
        <EntityListPicker<string>
          label={labels.search}
          placeholder={labels.search}
          autoFocus
          value={null}
          items={items}
          labels={{ ...pickerLabels, noMatches: labels.empty }}
          onChange={(entry) => {
            if (!entry) return;
            onOpenChange(false);
            onPick(entry.id);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
